import AbstractPeriodReportSectionVisitor from "../../../../models/accounting/periodReport/section/AbstractPeriodReportSectionVisitor.js";
import DOMExporter from "../../../../core/pdfExport/DOMExporter.js";
import html2canvas from "html2canvas";

/**
 * Export period report sections into pdf data to be used by PDFMake.
 */
export default class PeriodReportSectionPDFExporter extends AbstractPeriodReportSectionVisitor {
	constructor() {
		super(...arguments);

		// Store the list of canvas captures (think of it as screenshots) of DOM
		// elements.  Each key is a selector used to find a DOM element, the
		// corresponding value is a PDFMake image structure with the canvas
		// content of the DOM element.
		this._domCaptures = {};
	}

	async export(sections) {
		await this._prepareDOMCaptures();

		return {
			content: this.visitAllEnabled(sections),
			styles: this._getStyles(),
		};
	}

	visitBusinessOverviewSection(section) {
		return this._exportSection(section, ".business-overview-report");
	}

	visitIncomeStatementSection(section) {
		return this._exportSection(section, ".condensed-income-statement");
	}

	visitBalanceSheetSection(section) {
		return this._exportSection(section, ".condensed-balance-sheet");
	}

	visitKeyfigureSection(section) {
		return this._exportSection(section, "#keyfigures-container");
	}

	visitAgedAccountsReceivablesSection(section) {
		return this._exportSection(
			section,
			".aged-accounts-receivables-summary",
		);
	}

	visitAgedAccountsPayablesSection(section) {
		return this._exportSection(section, ".aged-accounts-payables-summary");
	}

	visitAttachmentSection(section) {
		if (!section.hasComment()) {
			return [];
		}

		return [
			this._exportSectionTitle(section),
			this._exportSectionComment(section),
		];
	}

	_exportSection(section, domSelector) {
		return [
			this._exportSectionTitle(section),
			this._domCaptures[domSelector],
			this._exportSectionComment(section),
		];
	}

	_exportSectionTitle(section) {
		return {
			layout: {
				hLineWidth: (i) => i,
				vLineWidth: () => 0,
				hLineColor: () => "#d0d0d0",
			},
			table: {
				headerRows: 0,
				widths: ["*"],
				body: [
					[
						{
							text: section.getTitle().toString().toUpperCase(),
							style: "section-title",
						},
					],
				],
			},
			style: "section-title-container",
		};
	}

	_exportSectionComment(section) {
		if (!section.hasComment()) {
			return {};
		}

		const exporter = new DOMExporter();

		const result = exporter.exportFromHTML(section.getComment());

		return {
			layout: {
				hLineWidth: () => 1,
				vLineWidth: () => 0,
				hLineColor: () => "#d0d0d0",
				paddingLeft: () => 10,
				paddingRight: () => 10,
				fillColor: () => "#fffaf1",
			},
			table: {
				headerRows: 0,
				widths: ["*"],
				body: [[result.content]],
			},
			style: "section-comment",
		};
	}

	_prepareDOMCaptures() {
		return this._captureSelectors([
			".business-overview-report",
			".condensed-income-statement",
			".condensed-balance-sheet",
			"#keyfigures-container",
			".aged-accounts-receivables-summary",
			".aged-accounts-payables-summary",
		]);
	}

	async _captureSelectors(selectors) {
		if (!selectors.length) {
			return;
		}

		// These promises are executed for side-effect only, we don't
		// care about their resolved values.
		await Promise.all(
			selectors.map((selector) => this._captureSelector(selector)),
		);
	}

	async _captureSelector(selector) {
		const element = document.querySelector(selector);
		// Accountants can disable period report sections. In this case,
		// the corresponding HTML is not rendered and element becomes undefined.
		if (!element) {
			return;
		}

		const canvas = await html2canvas(element, {
			width: this._a4Pixels().width,
		});

		this._domCaptures[selector] = {
			image: canvas.toDataURL("image/png", 1.0),
			// A4 images at 67 DPI minus left and right margins.
			// Otherwise the canvas export is blurry.
			//
			// The size is quite approximative, but given some
			// extensive testing this looks fairly good.
			width: this._a4Pixels().width / 1.6,
		};
	}

	/**
	 * A4 (21cmx29.7cm) dimensions in 108 DPI.
	 */
	_a4Pixels() {
		return {
			width: 892.5,
			height: 1263,
		};
	}

	_getStyles() {
		return {
			"section-title-container": {
				margin: [0, 40],
			},
			"section-title": {
				fontSize: 16,
				color: "#747474",
				bold: true,
				alignment: "center",
			},
			"section-comment": {
				marginTop: 10,
			},
		};
	}
}
